import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "react-router-dom";
import { Settings, Plug, Shield, Zap, UserCheck, Apple, Monitor, ChevronDown, Grid2X2, Chrome, Egg, Bot } from "lucide-react";
import { patchPolicySchema, PatchPolicyFormValues } from "@/components/patching/schema";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

// Import category components
import { SystemIntegrationSettings } from "@/components/settings/SystemIntegrationSettings";
import { IdentityProvidersSettings } from "@/components/settings/IdentityProvidersSettings";
import { GeneralSettings } from "@/components/settings/GeneralSettings";
import { AppleSettings } from "@/components/settings/AppleSettings";

type SettingsCategory = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

// General settings tabs
const generalCategories: SettingsCategory[] = [
  { id: "general", label: "General", icon: Settings },
  { id: "system", label: "Integrations", icon: Plug },
  { id: "identity", label: "Identity Providers", icon: UserCheck },
  { id: "security", label: "Security", icon: Shield },
  { id: "advanced", label: "Advanced", icon: Zap }
];

// Platform-specific settings
const platformCategories: SettingsCategory[] = [
  { id: "apple", label: "Apple", icon: Apple },
  { id: "android", label: "Android", icon: Bot },
  { id: "chromeos", label: "ChromeOS", icon: Chrome },
  { id: "linux", label: "Linux", icon: Egg },
  { id: "windows", label: "Windows", icon: Grid2X2 }
];

const allCategories = [...generalCategories, ...platformCategories];

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get("category");
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl || "general");

  // Update selected category when URL parameter changes
  useEffect(() => {
    if (categoryFromUrl && allCategories.find(cat => cat.id === categoryFromUrl)) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [categoryFromUrl]);

  const form = useForm<PatchPolicyFormValues>({
    resolver: zodResolver(patchPolicySchema),
    defaultValues: {
      name: "",
      description: "",
      schedule_type: "manual",
      restart_required: false,
      defer_max_times: 3,
      defer_duration_minutes: 60,
      enabled: true,
      update_method: "default",
      force_install_days: 90,
      allowed_days: [],
      show_notifications: true,
      notifications_timing: "all",
      allow_major_updates: true,
      allow_minor_updates: true,
      allow_security_updates: true,
      auto_download: true,
      auto_install_os_updates: false,
      auto_install_security_updates: true,
      enable_rsr_installation: false,
      enable_rsr_rollback: false,
      smtp_host: "",
      smtp_port: 587,
      smtp_username: "",
      smtp_password: "",
      smtp_from_address: "",
      apns_certificate: "",
      apns_certificate_password: "",
      abm_token: "",
      abm_organization_id: "",
      abm_server_token: ""
    }
  });

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setSearchParams({ category: value });
  };

  // Check if current selection is a platform
  const selectedPlatform = platformCategories.find(p => p.id === selectedCategory);
  const isPlatformSelected = !!selectedPlatform;

  const renderCategoryContent = () => {
    switch (selectedCategory) {
      case "general":
        return <GeneralSettings form={form} />;
      case "system":
        return <SystemIntegrationSettings form={form} />;
      case "apple":
        return <AppleSettings form={form} />;
      case "identity":
        return <IdentityProvidersSettings />;
      case "security":
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Shield className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Security Settings</h3>
            <p className="text-muted-foreground max-w-md">
              Security configurations will be available in a future update. Configure access controls, audit logs, and security policies.
            </p>
          </div>
        );
      case "advanced":
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Zap className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Advanced Settings</h3>
            <p className="text-muted-foreground max-w-md">
              Advanced configurations and integrations will be available in a future update. Manage custom workflows and API integrations.
            </p>
          </div>
        );
      case "android":
      case "chromeos":
      case "linux":
      case "windows":
        const platform = platformCategories.find(p => p.id === selectedCategory);
        const PlatformIcon = platform?.icon || Monitor;
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <PlatformIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{platform?.label} Settings</h3>
            <p className="text-muted-foreground max-w-md">
              {platform?.label} platform configurations will be available in a future update.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background w-full">
      <div className="w-full space-y-6 px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Settings</h1>
            <p className="text-muted-foreground mt-1">
              Manage your application settings and integrations
            </p>
          </div>
        </div>

        {/* Tabs with Platforms Dropdown */}
        <Tabs value={isPlatformSelected ? "" : selectedCategory} onValueChange={handleCategoryChange} className="space-y-6">
          <div className="flex items-center">
            <TabsList>
              {generalCategories.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className="hover:bg-transparent hover:text-current"
                >
                  <category.icon className="w-4 h-4 mr-2" />
                  {category.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Platforms Dropdown */}
            <div className={cn(
              "inline-flex h-9 items-center justify-center rounded-lg p-1 ml-2 w-[140px] transition-colors",
              selectedCategory === "apple" && "bg-muted border border-border shadow-sm",
              selectedCategory === "android" && "bg-green-600 border border-green-600 shadow-sm",
              selectedCategory === "chromeos" && "bg-red-600 border border-red-600 shadow-sm",
              selectedCategory === "linux" && "bg-zinc-900 border border-zinc-900 shadow-sm",
              selectedCategory === "windows" && "bg-blue-600 border border-blue-600 shadow-sm"
            )}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-full",
                      isPlatformSelected 
                        ? "bg-background text-muted-foreground shadow" 
                        : "bg-muted text-muted-foreground border border-border shadow-sm"
                    )}
                  >
                    {selectedPlatform ? (
                      <>
                        <selectedPlatform.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{selectedPlatform.label}</span>
                      </>
                    ) : (
                      <span>Platforms</span>
                    )}
                    <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="z-50 bg-popover">
                  {platformCategories.map((platform) => (
                    <DropdownMenuItem
                      key={platform.id}
                      onClick={() => handleCategoryChange(platform.id)}
                      className={cn(
                        "cursor-pointer",
                        selectedCategory === platform.id && "bg-accent"
                      )}
                    >
                      <platform.icon className="w-4 h-4 mr-2" />
                      {platform.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <TabsContent value={selectedCategory} className="mt-0" forceMount={isPlatformSelected ? true : undefined}>
            <Form {...form}>
              <form className="space-y-6">
                {renderCategoryContent()}
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}